import type { AppData } from "./AppContainer";
import {
  buildSelectedTargetSummary,
  normalizeSelectedTarget,
  type SelectedTarget,
} from "./selectedTarget";
import { buildWorkPanelDigestText } from "./workspaceCollections";

export interface WorkspaceSummarySnapshot {
  workstreams: number;
  openIssues: number;
}

export interface WorkspaceScreenContextInput {
  surface: string;
  activeApp?: Pick<AppData, "id" | "title"> | null;
  summary: WorkspaceSummarySnapshot;
  selectedTarget?: SelectedTarget | null;
}

function normalizeSurfaceLabel(surface: string): string {
  switch (surface) {
    case "life":
      return "Life";
    case "comms":
      return "Comms";
    case "files":
      return "Files";
    case "projects":
      return "Projects";
    case "conversations":
      return "Conversations";
    case "workstreams":
      return "Workstreams";
    case "settings":
      return "Settings";
    case "apps":
      return "Apps";
    default:
      return "Workspace";
  }
}

export function getWorkspaceScreenLabel(input: WorkspaceScreenContextInput): string {
  if (input.activeApp?.title?.trim()) {
    return input.activeApp.title.trim();
  }
  return normalizeSurfaceLabel(input.surface);
}

export function buildWorkspaceScreenSummary(input: WorkspaceScreenContextInput): string {
  const screenLabel = getWorkspaceScreenLabel(input);
  const digestText = buildWorkPanelDigestText(input.summary);
  const parts = [`Current workspace area: ${screenLabel}.`];
  const selectedTargetSummary = buildSelectedTargetSummary(input.selectedTarget ?? undefined);

  if (input.activeApp?.id) {
    parts.push(`Open app id: ${input.activeApp.id}.`);
  }

  if (input.surface === "files") {
    parts.push("This screen is about files, uploads, and generated outputs.");
  } else if (input.surface === "projects") {
    parts.push("This screen is about planned outcomes and project tracking.");
  } else if (input.surface === "workstreams") {
    parts.push("This screen is about ongoing loops and active follow-through.");
  } else if (input.surface === "conversations") {
    parts.push("This screen is about conversation history and returning to past chats.");
  } else if (input.surface === "life") {
    parts.push("This screen is about the operator's life context and active life items.");
  } else if (input.surface === "comms") {
    parts.push("This screen is about email drafts, reviews, and communication work.");
  } else if (input.surface === "settings") {
    parts.push("This screen is about account connections, limits, and preferences.");
  }

  if (selectedTargetSummary) {
    parts.push(selectedTargetSummary);
  }

  parts.push(`Workspace summary: ${digestText}`);
  return parts.join(" ");
}

export function buildWorkspaceBridgeContext(
  input: WorkspaceScreenContextInput,
): Record<string, unknown> {
  const screenLabel = getWorkspaceScreenLabel(input);
  const contextSummary = buildWorkspaceScreenSummary(input);
  const normalizedSelectedTarget = input.selectedTarget
    ? normalizeSelectedTarget(input.selectedTarget)
    : undefined;
  const selectionSummary = buildSelectedTargetSummary(normalizedSelectedTarget);

  return {
    workspaceSurface: input.surface,
    workspaceSurfaceLabel: screenLabel,
    sidebarNodeLabel: screenLabel,
    uiSurface: input.activeApp?.id ? `app:${input.activeApp.id}` : input.surface,
    ...(normalizedSelectedTarget ? { selectedTarget: normalizedSelectedTarget } : {}),
    ...(input.activeApp?.id ? { appId: input.activeApp.id } : {}),
    ...(input.activeApp?.title ? { appTitle: input.activeApp.title } : {}),
    workspaceDigestSummary: buildWorkPanelDigestText(input.summary),
    ...(selectionSummary ? { selectionSummary } : {}),
    contextSummary,
  };
}
